import type { OrderGroupDetails } from '@/entities/order/model'
import type { StateDefinition } from '@/entities/state/model'
import { useMantineTheme } from '@mantine/core'
import { useMemo } from 'react'
import { useStateConfigQuery, useStateDictionaryQuery } from '@/entities/state/model'
import { calculateAlertState } from './order.alertState'

/**
 * Hook that provides alert information for a group of order based on its state and elapsed time
 * @param {OrderGroupDetails} groupOrder - Group of order details
 * @returns Alert information including timer, animation state, severity, and formatted time
 * NOTE: Assumes first order in the group is representative of the group status
 */
export function useGroupOrderAlert(groupOrder: OrderGroupDetails) {
  const { data: stateConfig } = useStateConfigQuery()
  const { data: stateDictionary } = useStateDictionaryQuery()
  const { colors } = useMantineTheme()

  const firstOrder = groupOrder.orders[0]

  const defaultDisplayState = {
    orderDetailsTitle: firstOrder.status.toUpperCase(),
    color: colors.green[7],
    categoryTitle: firstOrder.status,
    stateDisplayName: firstOrder.status,
    orderDetailsSummary: `Order ${firstOrder.orderId}`,
  }

  const { alertState, displayState } = useMemo(() => {
    const stateDefinition: StateDefinition = stateDictionary?.[firstOrder.status] || {
      name: firstOrder.status,
      displayState: defaultDisplayState,
      eventTriggers: [],
      progressionLevel: 0,
      canAcknowledge: true,
      isActive: false,
      isError: false,
      shouldNotify: false,
      canForceTransition: false,
    }

    const alertState = calculateAlertState({
      order: firstOrder,
      targetState: stateDefinition,
      stateFeatures: stateConfig?.features,
      currentTime: new Date(),
    })

    return {
      alertState,
      displayState: stateDefinition.displayState || defaultDisplayState,
    }
  // eslint-disable-next-line react/exhaustive-deps
  }, [firstOrder, stateConfig, stateDictionary])

  return {
    alertState,
    displayState,
  }
}
